import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IdentificationBadge } from '@phosphor-icons/react';

import { getClubMembershipFee, setClubMembershipFee } from '@/api/notices';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useConfirm } from '@/components/ConfirmDialog';

// ClubMembershipFeeCard is the one club-wide annual membership fee: set once,
// paid by every member in every category. It lives INSIDE a category's
// "Avgifter" tab (next to that category's own training fees) because that's
// where an admin thinking about money looks — but it is emphatically not a
// per-category setting, so the card carries a "whole club" badge, names the
// category it is NOT scoped to, and routes every change through a confirm
// dialog stating the before/after amounts. Wide blast radius deserves a speed
// bump (CLAUDE.md). Under the hood it's a single standalone fee rule.

interface Props {
  slug: string;
  canManage: boolean;
  // The category whose drawer this card is rendered in. Used only in the copy —
  // "applies to every category, not just Senior" is the clearest way to say
  // "this control is wider than the panel it sits in".
  categoryName?: string;
}

// formatAmount renders a minor-unit amount as a human "1 200 kr" string.
function formatAmount(minor: number, currency: string): string {
  return `${(minor / 100).toLocaleString('sv-SE')} ${currency || 'kr'}`;
}

export function ClubMembershipFeeCard({ slug, canManage, categoryName }: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();
  const confirm = useConfirm();

  const [amount, setAmount] = useState('');
  const [seeded, setSeeded] = useState(false);

  const feeQuery = useQuery({
    queryKey: ['club-membership-fee', slug],
    queryFn: () => getClubMembershipFee(token, slug),
    enabled: !!token && !!slug,
  });

  // Seed the input once from the loaded value.
  useEffect(() => {
    if (seeded || !feeQuery.data) return;
    const minor = feeQuery.data.membership_minor;
    setAmount(minor != null ? String(minor / 100) : '');
    setSeeded(true);
  }, [seeded, feeQuery.data]);

  const saveM = useMutation({
    mutationFn: (minor: number) => setClubMembershipFee(token, slug, { membership_minor: minor }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['club-membership-fee', slug] });
      // Category fee views show membership as an inherited hint.
      void qc.invalidateQueries({ queryKey: ['category-fees', slug] });
      void qc.invalidateQueries({ queryKey: ['notices-config', slug] });
    },
  });

  const num = Number(amount.trim());
  const valid = amount.trim() !== '' && Number.isFinite(num) && num >= 0;
  const savedMinor = feeQuery.data?.membership_minor ?? null;
  const currency = feeQuery.data?.currency ?? 'kr';
  const dirty = valid && Math.round(num * 100) !== savedMinor;

  // Every change goes through the confirm dialog, naming both amounts so the
  // admin sees exactly what the whole club moves from and to.
  async function onSave() {
    const nextMinor = Math.round(num * 100);
    const ok = await confirm({
      title: t('notices.fees.clubMembershipConfirmTitle', {
        defaultValue: 'Change the membership fee for the whole club?',
      }),
      body: t('notices.fees.clubMembershipConfirmBody', {
        defaultValue:
          'This changes the annual membership fee from {{from}} to {{to}} for every member of the club, in every category and team — not just this one.',
        from:
          savedMinor != null
            ? formatAmount(savedMinor, currency)
            : t('notices.fees.membershipUnset', { defaultValue: 'not set yet' }),
        to: formatAmount(nextMinor, currency),
      }),
      confirmLabel: t('notices.fees.clubMembershipConfirmCta', {
        defaultValue: 'Change for the whole club',
      }),
    });
    if (ok) saveM.mutate(nextMinor);
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-muted/30 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <IdentificationBadge size={20} className="shrink-0 text-foreground/70" />
        <h3 className="font-heading text-sm font-semibold text-foreground">
          {t('notices.fees.clubMembershipTitle', { defaultValue: 'Club membership fee' })}
        </h3>
        <Badge variant="secondary">
          {t('notices.fees.clubMembershipScope', { defaultValue: 'Whole club' })}
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        {categoryName
          ? t('notices.fees.clubMembershipCategoryHelp', {
              defaultValue:
                'The annual fee every member pays — in every category, not just {{category}}. The fees below apply to {{category}} only.',
              category: categoryName,
            })
          : t('notices.fees.clubMembershipHelp', {
              defaultValue:
                'The annual membership fee every member pays, across all categories. Training fees are set per category below.',
            })}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">
            {t('notices.fees.membershipAmount', { defaultValue: 'Membership fee (kr)' })}
          </span>
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            value={amount}
            disabled={!canManage || feeQuery.isPending || saveM.isPending}
            onChange={(e) => setAmount(e.target.value)}
            className="w-40 rounded-pill"
          />
        </label>
        {canManage && (
          <Button type="button" disabled={!dirty || saveM.isPending} onClick={() => void onSave()}>
            {saveM.isPending ? t('common.saving') : t('common.save')}
          </Button>
        )}
      </div>
    </div>
  );
}
